import { UserRole } from '@hrms/shared';

export const ROLES_CHANGED_EVENT = 'hrms:roles-changed';
const STORAGE_KEY = 'hrms_custom_roles';
const ALL_ROLES_STORAGE_KEY = 'hrms_all_roles_cache';
const DELETED_ROLES_KEY = 'hrms_deleted_roles';

export function getDeletedRoles(): string[] {
  try {
    const raw = localStorage.getItem(DELETED_ROLES_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.map((s: string) => s.toLowerCase()) : [];
  } catch {
    return [];
  }
}

export function addDeletedRole(value: string): void {
  try {
    const list = getDeletedRoles();
    const val = value.trim().toLowerCase();
    if (!list.includes(val)) {
      list.push(val);
      localStorage.setItem(DELETED_ROLES_KEY, JSON.stringify(list));
    }
  } catch {}
}

export function clearDeletedRole(value: string): void {
  try {
    const list = getDeletedRoles();
    const val = value.trim().toLowerCase();
    const updated = list.filter((s) => s !== val);
    localStorage.setItem(DELETED_ROLES_KEY, JSON.stringify(updated));
  } catch {}
}

export interface StoredRole {
  value: string;
  label: string;
  category: 'executive' | 'standard';
  isDefault?: boolean;
}

export const DEFAULT_ROLES: StoredRole[] = [
  { value: UserRole.EMPLOYEE, label: 'Employee', category: 'standard', isDefault: true },
  { value: UserRole.ADMIN, label: 'Admin', category: 'standard', isDefault: true },
  { value: UserRole.SUPER_ADMIN, label: 'Super Admin', category: 'standard', isDefault: true },
];

export const CORE_SYSTEM_ROLES = [UserRole.EMPLOYEE, UserRole.ADMIN, UserRole.SUPER_ADMIN];

function getCustomRolesOnly(): StoredRole[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function setStoredRoles(roles: StoredRole[]): void {
  try {
    localStorage.setItem(ALL_ROLES_STORAGE_KEY, JSON.stringify(roles));
    // Also update custom roles list
    const custom = roles.filter((r) => !CORE_SYSTEM_ROLES.includes(r.value as UserRole));
    localStorage.setItem(STORAGE_KEY, JSON.stringify(custom));
  } catch (error) {
    console.error('Failed to set stored roles:', error);
  }
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(ROLES_CHANGED_EVENT, { detail: roles }));
  }
}

export function getAllStoredRoles(): StoredRole[] {
  const deleted = getDeletedRoles();

  try {
    const allCached = localStorage.getItem(ALL_ROLES_STORAGE_KEY);
    if (allCached) {
      const parsed = JSON.parse(allCached);
      if (Array.isArray(parsed) && parsed.length > 0) {
        return parsed.filter(
          (r) =>
            !deleted.includes(r.value.toLowerCase()) &&
            !(r.value.toLowerCase() === 'coo' && r.isDefault)
        );
      }
    }
  } catch {
    // fallback
  }

  const custom = getCustomRolesOnly();
  // Merge default roles with custom
  const map = new Map<string, StoredRole>();
  for (const r of DEFAULT_ROLES) {
    if (!deleted.includes(r.value.toLowerCase())) {
      map.set(r.value.toLowerCase(), { ...r });
    }
  }
  for (const c of custom) {
    if (!deleted.includes(c.value.toLowerCase())) {
      map.set(c.value.toLowerCase(), { ...c });
    }
  }
  return Array.from(map.values());
}

export function addCustomRole(
  name: string,
  category: 'executive' | 'standard' = 'executive'
): { success: boolean; error?: string; roles: StoredRole[] } {
  const trimmed = name.trim();
  if (!trimmed) {
    return { success: false, error: 'Role name cannot be empty', roles: getAllStoredRoles() };
  }

  if (trimmed.length > 50) {
    return { success: false, error: 'Role name must be 50 characters or fewer', roles: getAllStoredRoles() };
  }

  const all = getAllStoredRoles();
  const duplicate = all.some(
    (r) =>
      r.label.toLowerCase() === trimmed.toLowerCase() ||
      r.value.toLowerCase() === trimmed.toLowerCase().replace(/\s+/g, '_')
  );

  if (duplicate) {
    return { success: false, error: `Role "${trimmed}" already exists`, roles: all };
  }

  const value = trimmed.toLowerCase().replace(/\s+/g, '_');
  clearDeletedRole(value);

  const newRole: StoredRole = {
    value,
    label: trimmed,
    category,
    isDefault: false,
  };

  const updatedAll = [...all, newRole];
  setStoredRoles(updatedAll);

  return { success: true, roles: updatedAll };
}

export function updateCustomRole(
  oldValue: string,
  newLabel: string,
  category?: 'executive' | 'standard'
): { success: boolean; error?: string; roles: StoredRole[] } {
  const trimmedValue = oldValue.trim().toLowerCase();
  const trimmedLabel = newLabel.trim();

  if (!trimmedValue) {
    return { success: false, error: 'Role identifier is required', roles: getAllStoredRoles() };
  }
  if (!trimmedLabel) {
    return { success: false, error: 'Role name cannot be empty', roles: getAllStoredRoles() };
  }
  if (trimmedLabel.length > 50) {
    return { success: false, error: 'Role name must be 50 characters or fewer', roles: getAllStoredRoles() };
  }

  const all = getAllStoredRoles();
  const roleIndex = all.findIndex((r) => r.value.toLowerCase() === trimmedValue);
  if (roleIndex === -1) {
    return { success: false, error: `Role not found`, roles: all };
  }

  const duplicate = all.some(
    (r, i) => i !== roleIndex && r.label.toLowerCase() === trimmedLabel.toLowerCase()
  );
  if (duplicate) {
    return { success: false, error: `Role "${trimmedLabel}" already exists`, roles: all };
  }

  const existingRole = all[roleIndex];
  const updatedRole: StoredRole = {
    ...existingRole,
    label: trimmedLabel,
    ...(category ? { category } : {}),
  };

  const updatedAll = [...all];
  updatedAll[roleIndex] = updatedRole;
  setStoredRoles(updatedAll);

  return { success: true, roles: updatedAll };
}

export function removeCustomRole(value: string): { success: boolean; error?: string; roles: StoredRole[] } {
  const trimmedValue = value.trim().toLowerCase();
  if (CORE_SYSTEM_ROLES.some((r) => r.toLowerCase() === trimmedValue)) {
    return { success: false, error: 'System core roles (Employee, Admin, Super Admin) cannot be deleted', roles: getAllStoredRoles() };
  }

  addDeletedRole(trimmedValue);

  const all = getAllStoredRoles();
  const updatedAll = all.filter((r) => r.value.toLowerCase() !== trimmedValue);
  setStoredRoles(updatedAll);

  return { success: true, roles: updatedAll };
}
